import React, { useEffect, useRef } from "react";

// SASS
import "./Part6.scss";

// COMPONENTS
import Step from "../Step/Step";

// HELPERS
import animate from "../../utils/animate";

// LIBRARIES
import scrollama from "scrollama";

// ASSETS
import eyeImg from "../../assets/img/Eye-and-Nose.png";
import mirrorImg from "../../assets/img/Mirror.png";
import tearImg from "../../assets/img/Large-Tear.png";

export default function Part6({ step }) {
  // SCROLLAMA INSTANCE
  const scrolly = scrollama();

  // ANIMATION STEP DATA
  const animationSequence = [
    { stepNumber: "0", height: null, description: "spacer" },
    { stepNumber: "1", height: null, description: "show reveal1, reveal1a" },
    {
      stepNumber: "2",
      height: null,
      description: "hide hide 1, show reveal 3",
    },
    { stepNumber: "3", height: null, description: "show reveal 4" },
    { stepNumber: "4", height: null, description: "show reveal 5" },
    { stepNumber: "5", height: null, description: "spacer" },
    { stepNumber: "6", height: null, description: "spacer" },
  ];

  // REFS
  const hide1Ref = useRef(null);
  const reveal1Ref = useRef(null);
  const reveal1aRef = useRef(null);
  const reveal2Ref = useRef(null);
  const reveal3Ref = useRef(null);
  const reveal4Ref = useRef(null);
  const reveal5Ref = useRef(null);
  const animate1Ref = useRef(null);

  // RUN WHEN THE COMPONENT MOUNTS
  useEffect(() => {
    scrolly
      .setup({
        step: `.${step}`,
        debug: false,
        threshold: 1,
        progress: true,
      })
      .onStepEnter(({ element, direction }) => {
        const step = element.dataset.step;
        switch (step) {
          case "1": {
            animate.fadeInOnEnter(reveal1Ref.current, direction);
            animate.fadeInOnEnter(reveal1aRef.current, direction);
            break;
          }
          case "2": {
            animate.fadeOutOnEnter(hide1Ref.current, direction);
            animate.fadeInOnEnter(reveal2Ref.current, direction);
            animate.fadeInOnEnter(reveal3Ref.current, direction);
            break;
          }
          case "3": {
            animate.addClassOnEnter(animate1Ref.current, "lift", direction);
            animate.fadeInOnEnter(reveal4Ref.current, direction);
            break;
          }
          case "4": {
            animate.fadeInOnEnter(reveal5Ref.current, direction);
            break;
          }
          default:
            break;
        }
      })
      .onStepProgress(({ element, progress }) => {
        const step = element.dataset.step;
        switch (step) {
          default:
            break;
        }
      })
      .onStepExit(({ element, direction }) => {
        const step = element.dataset.step;
        switch (step) {
          case "1": {
            animate.fadeOutOnExit(reveal1Ref.current, direction);
            animate.fadeOutOnExit(reveal1aRef.current, direction);
            break;
          }
          case "2": {
            animate.fadeInOnExit(hide1Ref.current, direction);
            animate.fadeOutOnExit(reveal2Ref.current, direction);
            animate.fadeOutOnExit(reveal3Ref.current, direction);
            break;
          }
          case "3": {
            animate.removeClassOnExit(animate1Ref.current, "lift", direction);
            animate.fadeOutOnExit(reveal4Ref.current, direction);
            break;
          }
          case "4": {
            animate.fadeOutOnExit(reveal5Ref.current, direction);
            break;
          }
          default:
            break;
        }
      });
  }, [scrolly, step]);

  // CUSTOM FUNCTIONS

  return (
    <section className="wrapper">
      <div className="sticky-container">
        {/* STICKY */}
        <div className="part6">
          <div className="part6__text part6__text--top">
            <div className="p1 hide hide--1" ref={hide1Ref}>
              As your body produces tears to keep the eyes protected,{" "}
              <span className="reveal reveal--1" ref={reveal1Ref}>
                the tears drain into tear ducts located at the corners of the
                eye near the bridge of the nose.
              </span>
            </div>
            <div className="p2 reveal reveal--2" ref={reveal2Ref}>
              If you look in the mirror, the opening of your tear duct looks
              like a pinpoint-sized dot.
            </div>
          </div>
          <div className="part6__illustrations">
            <div className="part6__illustrations-eye">
              <img src={eyeImg} alt="" className="img-fluid" />
            </div>
            <div
              className="part6__illustrations-tear-duct"
              ref={animate1Ref}
            ></div>
            <div
              className="part6__illustrations-tear reveal reveal--1a"
              ref={reveal1aRef}
            >
              <img src={tearImg} alt="" className="img-fluid" />
            </div>
            <div
              className="part6__illustrations-mirror reveal reveal--3"
              ref={reveal3Ref}
            >
              <img src={mirrorImg} alt="" className="img-fluid" />
            </div>
            <div
              className="part6__illustrations--circle reveal reveal--4"
              ref={reveal4Ref}
            ></div>
          </div>
          <div
            className="part6__text part6__text--bottom reveal reveal--5"
            ref={reveal5Ref}
          >
            These ducts connect and drain liquid from your eyes to your nose and
            throat.
          </div>
        </div>
      </div>

      {/* STEPS */}
      {animationSequence.map(({ stepNumber, height, description, offset }) => {
        return (
          <Step
            stepSection={step}
            stepNumber={stepNumber}
            stepDescription={description}
            height={height}
            key={stepNumber}
            offset={offset}
          />
        );
      })}
    </section>
  );
}
